'use client'

import { useRouter } from 'next/navigation'
import { Suspense, useState } from 'react'
import ReferralHandler from '@/components/referral/ReferralHandler'
import WebViewDetector from '@/components/webview/WebViewDetector'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import { X } from 'lucide-react'

export default function HomePage() {
  const router = useRouter()
  const [showChoiceModal, setShowChoiceModal] = useState(false)
  const [checking, setChecking] = useState(false)

  const handleFreeTrialClick = async () => {
    setChecking(true)
    try {
      // 檢查用戶是否已登入
      const { data: { user } } = await supabase.auth.getUser()
      
      if (user) {
        // 檢查訂閱權限
        try {
          const res = await fetch('/api/me/entitlement', { 
            cache: 'no-store',
            credentials: 'include'
          })
          if (res.ok) {
            const data = await res.json()
            const entitlement = data.entitlement
            const now = Date.now()
            const hasValidSubscription = entitlement?.endAt && new Date(entitlement.endAt).getTime() > now
            
            if (hasValidSubscription && entitlement.tier !== 'None') {
              // 有訂閱權限 → 顯示選擇視窗
              setShowChoiceModal(true)
              setChecking(false)
              return
            }
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('檢查訂閱權限失敗:', error)
          }
        }
      }
      
      // 無訂閱權限或未登入 → 直接進入 try 頁面
      router.push('/try/gojuon')
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('檢查用戶狀態失敗:', error)
      }
      // 發生錯誤時，直接進入 try 頁面
      router.push('/try/gojuon')
    } finally {
      setChecking(false)
    }
  }

  return (
    <div 
      className="min-h-screen text-white relative"
      style={{
        backgroundImage: 'url(/images/homepage-background.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#000000' // 黑色背景作為後備，確保在所有設備上都是黑色
      }}
    >
      {/* WebView 檢測與提示 */}
      <WebViewDetector onlyOnHomepage={true} />
      
      {/* 推廣追蹤處理 */}
      <Suspense fallback={null}>
        <ReferralHandler />
      </Suspense>
      
      {/* 主要內容區域 - 只保留按鈕，置中下方 */}
      <div className="relative z-10 min-h-screen flex flex-col justify-end items-center px-5 pb-12 gap-4">
        <button
          onClick={handleFreeTrialClick}
          disabled={checking}
          className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg text-base transition-colors w-full max-w-[200px] shadow-lg"
        >
          {checking ? '檢查中...' : '✨ 免費試用'}
        </button>
      </div>

      {/* 選擇視窗：有訂閱權限的用戶 */}
      {showChoiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <Card className="w-full max-w-md bg-gray-900 border-gray-700 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">選擇進入方式</CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowChoiceModal(false)}
                className="text-gray-400 hover:text-white min-h-[44px] min-w-[44px]"
              >
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-300 text-sm">
                您已有訂閱權限，請選擇要進入的頁面：
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => {
                    setShowChoiceModal(false)
                    router.push('/try/gojuon')
                  }}
                  variant="outline"
                  className="w-full"
                >
                  繼續試用頁面
                </Button>
                <Button
                  onClick={() => {
                    setShowChoiceModal(false)
                    router.push('/dashboard')
                  }}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600"
                >
                  進入正式頁面
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}